package one.healthium.featureconfig.data

import co.touchlab.kermit.Logger
import dev.gitlive.firebase.remoteconfig.FirebaseRemoteConfig
import dev.gitlive.firebase.remoteconfig.get
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import one.healthium.featureconfig.domain.ConfigKey
import one.healthium.featureconfig.domain.ConfigType
import one.healthium.featureconfig.domain.FeatureConfigRepository
import one.healthium.featureconfig.domain.FeatureConfigSchema
import kotlin.time.Duration.Companion.hours
import kotlin.time.TimeMark
import kotlin.time.TimeSource

internal class FirebaseFeatureConfigRepository(
    private val remoteConfig: FirebaseRemoteConfig,
    private val schema: FeatureConfigSchema,
) : FeatureConfigRepository {

    private val defaultsMutex = Mutex()
    private val refreshMutex = Mutex()
    private var defaultsApplied = false
    private var lastRefreshAttemptAt: TimeMark? = null

    @Suppress("UNCHECKED_CAST")
    override suspend fun <T> get(key: ConfigKey<T>): T {
        ensureDefaults()
        refreshIfRequired()
        return runCatching {
            when (key.type) {
                ConfigType.Boolean -> remoteConfig.get<Boolean>(key.name) as T
                ConfigType.String -> remoteConfig.get<String>(key.name) as T
                ConfigType.Long -> remoteConfig.get<Long>(key.name) as T
                ConfigType.Double -> remoteConfig.get<Double>(key.name) as T
            }
        }.getOrElse { error ->
            Logger.e("Feature config read failed for key=${key.name}", error)
            key.defaultValue
        }
    }

    private suspend fun ensureDefaults() {
        if (defaultsApplied) return

        defaultsMutex.withLock {
            if (defaultsApplied) return

            runCatching {
                val defaults = schema.keys.map { key -> key.name to key.defaultValue }
                remoteConfig.setDefaults(*defaults.toTypedArray())
                defaultsApplied = true
            }.onFailure { error ->
                Logger.e("Feature config defaults setup failed", error)
            }
        }
    }

    private suspend fun refreshIfRequired() {
        if (!isRefreshRequired()) return

        refreshMutex.withLock {
            if (!isRefreshRequired()) return
            lastRefreshAttemptAt = TimeSource.Monotonic.markNow()
            runCatching {
                remoteConfig.fetchAndActivate()
            }.onFailure { error ->
                Logger.e("Feature config refresh failed", error)
            }
        }
    }

    private fun isRefreshRequired(): Boolean {
        val mark = lastRefreshAttemptAt ?: return true
        return mark.elapsedNow() >= MIN_REFRESH_INTERVAL
    }

    private companion object {
        val MIN_REFRESH_INTERVAL = 6.hours
    }
}
